import axios from "axios";
import { API_BASE_URL } from "../config";

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
        "Content-Type": "application/json",
    },
});

// request interceptor for adding auth token to header
axiosInstance.interceptors.request.use(
    (config) => {
        config.headers['Authorization'] = `Bearer ${localStorage.getItem('authToken')}`;
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// response interceptor for handling errors globally
axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Redirect to login if unauthorized
            window.location.href = "/login";
        }
        return Promise.reject(error);
    }
);

export async function login(username, password) {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
        throw new Error("Invalid credentials");
    }

    const responseBody = await response.json();

    const authToken = responseBody.data.authToken;

    localStorage.setItem("authToken", authToken);
    localStorage.setItem("authTokenExpiresAt", responseBody.data.authTokenExpiresAt);
    localStorage.setItem("user", JSON.stringify(responseBody.data.user));

    return authToken;
}

// export async function fetchStats(date) {
//     const token = localStorage.getItem("authToken");
//     if (!token) {
//         return null;
//     }

//     const response = await fetch(`${API_BASE_URL}/stats?date=${date}`, {
//         headers: { Authorization: `Bearer ${token}` },
//     });

//     if (response.status != 200) {
//         const responseBody = await response.json();
//         throw new Error(responseBody.message);
//     }

//     const responseBody = await response.json();
//     return responseBody;
// }

export async function fetchStats(date) {
    const response = await axiosInstance.get(`/stats?date=${date}`);

    if (response.status != 200) {
        throw new Error(response.data.message);
    }

    return response.data;
}

export async function fetchOptionChainData(indexName, expiryDate) {
    const response = await axiosInstance.get(`/optionChain?index_name=${indexName}&expiry_date=${expiryDate}`);

    if (response.status != 200) {
        throw new Error(response.data.message);
    }

    return response.data;
}

export async function fetchOpenPositionsData({ user_account_id }) {
    const query = user_account_id ? `?user_account_id=${user_account_id}` : '';
    const response = await axiosInstance.get(`/openPositions${query}`);

    if (response.status != 200) {
        throw new Error(response.data.message);
    }

    return response.data;
}

export async function placeOrder(orderDetails) {
    const response = await axiosInstance.post(`/manualTrades`, orderDetails);

    if (response.status != 200) {
        throw new Error(response.data.message);
    }

    return response.data;
}

export async function modifyTrade(tradeId, orderDetails) {
    const response = await axiosInstance.put(`/manualTrades/${tradeId}`, orderDetails);

    if (response.status != 200) {
        throw new Error(response.data.message);
    }

    return response.data;
}

export async function exitTrade(tradeId) {
    const response = await axiosInstance.delete(`/manualTrades/${tradeId}`);

    if (response.status != 200) {
        throw new Error(response.data.message);
    }

    return response.data;
}

export async function positionSizeCalc(payload) {
    // payload: { user_account_id, instrument_id, stop_loss_price, target_price? }
    const response = await axiosInstance.post(`/positionSizeCalculator`, payload);
    if (response.status != 200) {
        throw new Error(response.data.message);
    }
    return response.data;
}

export async function fetchTradesData(broker, date) {
    const response = await axiosInstance.get(`/trades?&broker=${broker}&date=${date}&status=closed&buy_signal=manual&sort_by=id&sort_order=desc&includeStats=1`);

    if (response.status != 200) {
        throw new Error(response.data.message);
    }

    return response.data;
}

export async function fetchStatsData(broker, date) {
    const response = await axiosInstance.get(`/stats?broker=${broker}&date=${date}`);

    if (response.status != 200) {
        throw new Error(response.data.message);
    }

    return response.data;
}

export async function fetchDeploymentData(id) {
    const response = await axiosInstance.get(`/deployments/${id}`);

    if (response.status != 200) {
        throw new Error(response.data.message);
    }

    return response.data;
}

export async function terminateDeployment(deploymentId) {
    const response = await axiosInstance.delete(`/deployments/${deploymentId}`);

    if (response.status != 200) {
        throw new Error(response.data.message);
    }

    return response.data;
}

export async function fetchUserAccounts() {
    const response = await axiosInstance.get(`/userAccounts`);
    if (response.status != 200) {
        throw new Error(response.data.message);
    }
    return response.data;
}

export async function fetchUserAccountHistory(userAccountId) {
    const response = await axiosInstance.get(`/userAccounts/${userAccountId}/history`);
    if (response.status != 200) {
        throw new Error(response.data.message);
    }
    return response.data;
}

export async function fetchrecommendedInstruments(indexName, expiryDate, allowCachedData) {
    const response = await axiosInstance.get(`/suggestedInstruments?index_name=${indexName}&expiry_date=${expiryDate}&allow_cached_data=${allowCachedData}`);
    if (response.status != 200) {
        throw new Error(response.data.message);
    }
    return response.data;
}

export async function fetchActiveInstruments() {
    const response = await axiosInstance.get(`/activeInstruments`);
    if (response.status != 200) {
        throw new Error(response.data.message);
    }
    return response.data;
}

export async function fetchOptionChainConfigs() {
    const response = await axiosInstance.get(`/optionChainConfigs`);
    if (response.status != 200) {
        throw new Error(response.data.message);
    }
    return response.data;
}
